import { useEffect, useState } from "react";
import { Box, Typography, Stack, Autocomplete, TextField, Select, MenuItem, Button, Chip } from "@mui/material";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import dayjs from "dayjs";
import AgentBoBApi from "../../../api/AgentBoBApi";
import ImportCutoffAPI from "../../../api/ImportCutoffAPI";
import useFetchAgentsOptions from "../../../sharedhooks/Agents/useFetchAllAgents";
import ConfirmationModal from "../../../components/confirmation-modal";
import { generateButtonSx } from "../../../utils/elementor-style-override";

const formatPeriod = (period) => dayjs(`${period}-01`).format("MMMM YYYY");

/**
 * The plain-language scope of an export. Both fields are optional and an empty one means
 * "all", so every combination reads as one sentence instead of needing a mode switch.
 */
const describeScope = (agents, period) => {
	const who = agents.length === 0 ? "every agent" : agents.length === 1 ? agents[0].display_name : `${agents.length} selected agents`;
	const when = period ? `the ${formatPeriod(period)} cutoff` : "all cutoffs";

	return `Every policy for ${who}, across ${when}, with each client's notes and document links.`;
};

/**
 * Admin Settings › Data Export: Book of Business for any set of agents and one or all cutoffs.
 */
export default function DataExport({ showSnackbar }) {
	const { agents, loading: agentsLoading } = useFetchAgentsOptions();
	const [periods, setPeriods] = useState([]);
	const [selectedAgents, setSelectedAgents] = useState([]);
	const [period, setPeriod] = useState("");
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [exporting, setExporting] = useState(false);

	useEffect(() => {
		ImportCutoffAPI.list()
			.then((list) => setPeriods(list.map((entry) => entry.period)))
			.catch((error) => showSnackbar?.(error.message || "Could not load the cutoffs.", "error"));
	}, []);

	const handleExport = async () => {
		setExporting(true);

		try {
			const data = await AgentBoBApi.exportAgentBoBBulk({ agentIds: selectedAgents.map((a) => a.ID), period });

			if (!data.rows?.length) {
				showSnackbar?.("Nothing to export: no policies match that selection.", "info");
				return;
			}

			// Loaded on demand: ExcelJS is large and most visits never export.
			const { default: exportBoBExcel } = await import("../../ClientList/utils/exportBoBExcel.js");
			await exportBoBExcel(data);
			showSnackbar?.(`Exported ${data.rows.length.toLocaleString()} policies.`, "success");
		} catch (error) {
			showSnackbar?.(error.message || "The export failed. Please try again.", "error");
		} finally {
			setExporting(false);
			setConfirmOpen(false);
		}
	};

	return (
		<Box>
			<Typography variant="h6" mb={0.5}>
				Agent's BoB Export
			</Typography>
			<Typography sx={{ fontSize: 12, color: "text.secondary", mb: 2.5 }}>
				Download books of business as an Excel file. Leave a field empty to include all of them.
			</Typography>

			<Stack direction={{ xs: "column", md: "row" }} gap={2} alignItems={{ xs: "stretch", md: "flex-start" }}>
				<Box flex={2} minWidth={0}>
					<Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.75 }}>Agents</Typography>
					<Autocomplete
						multiple
						size="small"
						options={agents || []}
						loading={agentsLoading}
						value={selectedAgents}
						onChange={(e, value) => setSelectedAgents(value)}
						disableCloseOnSelect
						getOptionLabel={(option) => option?.display_name || ""}
						isOptionEqualToValue={(option, value) => option?.ID === value?.ID}
						sx={{
							// The theme's global button styles paint these blue; keep them as plain icons.
							"& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator": {
								backgroundColor: "transparent !important",
								color: "rgba(0, 0, 0, 0.54)",
								...generateButtonSx({ bg: "#EBF5FF !important", text: "#1D54A2" }, { bg: "transparent !important", text: "#1D54A2" }),
							},
						}}
						slotProps={{
							popper: {
								sx: { "& .MuiAutocomplete-paper": { width: "inherit !important" } },
							},
						}}
						renderTags={(value, getTagProps) =>
							value.map((option, index) => (
								<Chip {...getTagProps({ index })} key={option.ID} size="small" label={option.display_name} />
							))
						}
						renderInput={(params) => (
							<TextField
								{...params}
								placeholder={selectedAgents.length ? "" : "All agents"}
								sx={{ "& .MuiAutocomplete-input": { border: "none !important" } }}
							/>
						)}
					/>
				</Box>

				<Box flex={1} minWidth={{ md: 200 }}>
					<Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.75 }}>Cutoff</Typography>
					<Select size="small" fullWidth displayEmpty value={period} onChange={(e) => setPeriod(e.target.value)}>
						<MenuItem value="">All cutoffs</MenuItem>
						{periods.map((p) => (
							<MenuItem key={p} value={p}>
								{formatPeriod(p)}
							</MenuItem>
						))}
					</Select>
				</Box>
			</Stack>

			<Stack
				direction={{ xs: "column", sm: "row" }}
				alignItems={{ xs: "stretch", sm: "center" }}
				justifyContent="space-between"
				gap={2}
				sx={{ mt: 2.5, p: 2, bgcolor: "#F5F8FC", border: "1px solid #E8EDF2", borderRadius: "12px" }}
			>
				<Typography sx={{ fontSize: 13, color: "#1D54A2" }}>{describeScope(selectedAgents, period)}</Typography>
				<Button
					variant="contained"
					startIcon={<SystemUpdateAltIcon />}
					onClick={() => setConfirmOpen(true)}
					disabled={exporting}
					sx={{
						whiteSpace: "nowrap",
						flexShrink: 0,
						...generateButtonSx({ bg: "#F4F4F5 !important", text: "#1D54A2" }, { bg: "#F4F4F5 !important", text: "#1D54A2" }),
					}}
				>
					Export
				</Button>
			</Stack>

			<ConfirmationModal
				open={confirmOpen}
				onClose={() => !exporting && setConfirmOpen(false)}
				onConfirm={handleExport}
				title="Export books of business?"
				description={describeScope(selectedAgents, period)}
				confirmText="Export"
				cancelText="Cancel"
				isLoading={exporting}
			/>
		</Box>
	);
}
